import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  TextField,
  TextareaAutosize,
  Typography,
} from "@material-ui/core";
import { InsightsReportDTO, InsightsReportUpdateDTO } from "groopit-js/client";
import React, { useCallback, useEffect, useState } from "react";
import {
  getReportCreator,
  getReportDate,
  getReportFilter,
  getReportTitle,
} from "./InsightsReport";
import useLatestRef from "../react/react-use/useLatestRef";
import { useGroopitApiContext } from "groopit-js";
import { useInsightsPanelContext } from "./InsightsPanelContext";
import { SaveIcon } from "../Icons/SaveIcon";
import { CloseIcon } from "../Icons/CloseIcon";
import { MoreVert } from "@material-ui/icons";

interface InsightsReportEditorProps {
  report: InsightsReportDTO;
  isNewlyCreated: boolean;
}

export const InsightsReportEditor = (
  props: InsightsReportEditorProps,
): JSX.Element => {
  const { isNewlyCreated, report: originalReport } = { ...props };
  const insightsPanelContext = useInsightsPanelContext();
  const api = useLatestRef(useGroopitApiContext());
  const [report, setReport] = useState(props.report);
  const [reportMenuAnchor, setReportMenuAnchor] = useState<HTMLElement>();
  const isReportMenuOpen = Boolean(reportMenuAnchor);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const saveReport = useCallback(() => {
    api.current.insights
      .updateInsightsReport(
        report.id,
        new InsightsReportUpdateDTO({
          ...report,
        }),
      )
      .then((updatedReport) => {
        setReport(updatedReport);
        insightsPanelContext.onReportUpdated(updatedReport);
        insightsPanelContext.setSelectedReport(null);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [api, insightsPanelContext, report]);

  const discardChanges = useCallback(() => {
    if (isNewlyCreated) {
      setDeleteConfirmationOpen(true);
    } else {
      insightsPanelContext.setSelectedReport({
        report: originalReport,
        isEditMode: false,
        isNewlyCreated: false,
      });
    }
  }, [isNewlyCreated, insightsPanelContext, originalReport]);

  const deleteReport = useCallback(() => {
    api.current.insights
      .deleteInsightsReport(report.id)
      .then(() => {
        insightsPanelContext.onReportDeleted(report.id);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [api, insightsPanelContext, report]);

  useEffect(() => {
    setReport(originalReport);
  }, [originalReport]);

  return (
    <div>
      <div className="flex border-app-slateblue border-b-2 border-t-2 pb-2 pt-4">
        <div className="flex-grow space-y-2">
          <div
            className="text-display-headline text-color-secondary"
            style={{ padding: 0 }}
          >
            <TextField
              fullWidth
              variant="outlined"
              label="Headline"
              value={getReportTitle(report)}
              onChange={(event) => {
                const title = event.target.value.slice(0, 150);
                setReport(
                  (prev) =>
                    new InsightsReportDTO({
                      ...prev,
                      title: title ?? getReportTitle(report),
                    }),
                );
              }}
            />
          </div>
          <div className="italic">{getReportCreator(report)}</div>
          <div className="italic">
            <TextField
              fullWidth
              variant="outlined"
              label="Date"
              value={getReportDate(report)}
              onChange={(event) => {
                const timestamp = event.target.value;
                setReport(
                  (prev) =>
                    new InsightsReportDTO({
                      ...prev,
                      timestampOverride: timestamp,
                    }),
                );
              }}
            />
          </div>
          <div className="italic">
            <TextField
              fullWidth
              variant="outlined"
              label="Source"
              multiline
              value={getReportFilter(report)}
              onChange={(event) => {
                const source = event.target.value;
                setReport(
                  (prev) =>
                    new InsightsReportDTO({
                      ...prev,
                      sourceOverride: source,
                    }),
                );
              }}
            />
          </div>
        </div>
        <div className="flex flex-col text-app-slateblue">
          <IconButton
            id="filter-data-menu-button"
            className="place-self-start"
            color="inherit"
            aria-controls="filter-data-menu"
            aria-haspopup="true"
            aria-expanded={isReportMenuOpen ? "true" : undefined}
            aria-label="Filter Data"
            onClick={(event) => setReportMenuAnchor(event.currentTarget)}
          >
            <MoreVert />
          </IconButton>
          <Menu
            id="filter-data-menu"
            anchorEl={reportMenuAnchor}
            open={isReportMenuOpen}
            onClose={() => setReportMenuAnchor(undefined)}
            MenuListProps={{
              "aria-labelledby": "filter-data-menu-button",
            }}
          >
            <MenuItem onClick={saveReport}>
              <ListItemIcon>
                <SaveIcon className="text-app-gray-02" />
              </ListItemIcon>
              <Typography>Save</Typography>
            </MenuItem>
            <MenuItem onClick={discardChanges}>
              <ListItemIcon>
                <CloseIcon className="text-app-gray-02" />
              </ListItemIcon>
              <Typography>Discard</Typography>
            </MenuItem>
          </Menu>
          {isNewlyCreated && (
            <Dialog
              open={deleteConfirmationOpen}
              onClose={() => setDeleteConfirmationOpen(false)}
              aria-labelledby="delete-confirmation-title"
              aria-describedby="delete-confirmation-description"
            >
              <DialogTitle id="delete-confirmation-title">Delete</DialogTitle>
              <DialogContent>
                <DialogContentText id="delete-confirmation-description">
                  Closing this summary will permanently delete it. Do you want
                  to proceed?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  color="default"
                  variant="outlined"
                  onClick={(event) => {
                    setDeleteConfirmationOpen(false);
                    event.stopPropagation();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={(event) => {
                    deleteReport();
                    setDeleteConfirmationOpen(false);
                    event.stopPropagation();
                  }}
                >
                  Delete
                </Button>
              </DialogActions>
            </Dialog>
          )}
        </div>
      </div>
      <TextareaAutosize
        className="mt-4 w-full overflow-auto"
        value={report.content}
        onChange={(event) => {
          const content = event.target.value;
          setReport(
            (prev) => new InsightsReportDTO({ ...prev, content: content }),
          );
        }}
      />
    </div>
  );
};
